import React from 'react';
import { useQuery } from '@apollo/client';
import MaterialTable, { MTableToolbar } from 'material-table';
import { Typography, Tooltip } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { GET_INACTIVE_WELLS } from '../../components/wells/WellQueries';

const TableStyles = makeStyles(() => ({
  title: {
    color: '#1976d2',
    fontSize: '1.75rem',
    textAlign: 'center',
    textDecoration: 'underline',
    marginBottom: '1rem',
  },
  commentsCell: {
    width: '100px',
    textAlign: 'center',
    padding: '8px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  tooltip: {
    backgroundColor: '#fff',
    color: '#000',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
    fontSize: '0.875rem',
    maxWidth: '400px',
    padding: '8px 12px',
    border: '1px solid #ccc',
  },
}));

const SortWellsByAlphabet = (WellArr = []) => {
  let sorted = [...WellArr];
  return sorted.sort((a, b) => {
    if (a.name.toLowerCase() > b.name.toLowerCase()) {
      return 1;
    } else if (a.name.toLowerCase() < b.name.toLowerCase()) {
      return -1;
    } else {
      return 0;
    }
  });
};

const InactiveWells = () => {
  const classes = TableStyles();
  const { loading, error, data } = useQuery(GET_INACTIVE_WELLS);
  const [highlightedRowID, setHighlightedRow] = React.useState(null);

  // Loading state
  if (loading) {
    return <Typography>Loading inactive wells...</Typography>;
  }

  // Error state
  if (error) {
    console.error(error);
    return <Typography variant="h5">Error loading inactive wells: {error.message}</Typography>;
  }

  // Data from query
  const wells = SortWellsByAlphabet(data?.Well || []);

  // Headers for CSV export
  const headers = [
    { label: 'Well Name', key: 'name' },
    { label: 'API #', key: 'apiNumber' },
    { label: 'Operator', key: 'operator' },
    { label: 'RRC Lease #', key: 'rrcLeaseNumber' },
    { label: 'Permit', key: 'permitNumber' },
    { label: 'land ID', key: 'landId' },
    { label: 'Abstract', key: 'abstract' },
    { label: 'Status', key: 'status' },
    { label: 'Spud Date', key: 'spudDate' },
    { label: 'Rig Release', key: 'rigReleaseDate' },
    { label: 'Date Producing', key: 'dateProducing' },
    { label: 'Date In Pay', key: 'dateInPay' },
    { label: 'OGL', key: 'OGL' },
    { label: 'Location', key: 'location' },
    { label: 'DecProdUnit', key: 'decProdUnit' },
    { label: 'DPU', key: 'dpu' },
    { label: 'Producing Formation', key: 'producingFormation' },
    { label: 'Length of Lateral', key: 'lengthOfLateral' },
    { label: 'RRC Field', key: 'rrcField' },
    { label: 'Payor', key: 'payor' }, // Adjusted from 'oilPayor' to match column
    { label: 'Operator', key: 'operator' },
    { label: 'MES DOI', key: 'mesDOI' },
    { label: 'MPI DOI', key: 'mpiDOI' },
    { label: 'SRO DOI', key: 'sroDOI' },
    { label: 'Comments', key: 'notes' },
    { label: 'RRC Lease Notes', key: 'rrcLeaseNotes' },
    { label: 'Operator History', key: 'operatorHistory' },
    { label: 'Unit', key: 'unit' },
  ];

  const columns = [
    { title: 'Well Name', field: 'name', cellStyle: { whiteSpace: 'nowrap', textAlign: 'center' } },
    { title: 'API #', field: 'apiNumber', cellStyle: { whiteSpace: 'nowrap', textAlign: 'center' } },
    { title: 'Rcc Lease', field: 'rrcLeaseNumber' },
    { title: 'Permit', field: 'permitNumber' },
    {
      title: 'Land Id',
      field: 'landId',
      render: (rowData) => rowData.drilledOn.map((e) => e.id).join(', '),
      cellStyle: { whiteSpace: 'nowrap', textAlign: 'center' },
    },
    { title: 'Abstract', field: 'abstract' },
    { title: 'Status', field: 'status' },
    { title: 'Spud Date', field: 'spudDate' },
    { title: 'Rig Release', field: 'rigReleaseDate' },
    { title: 'Date Producing', field: 'dateProducing' },
    { title: 'Date In Pay', field: 'dateInPay' },
    { title: 'OGL', field: 'OGL' },
    { title: 'Location', field: 'location' },
    { title: 'DecProdUnit', field: 'decProdUnit' },
    { title: 'DPU', field: 'dpu', cellStyle: { whiteSpace: 'nowrap', textAlign: 'center' } },
    { title: 'Producing Formation', field: 'producingFormation' },
    { title: 'Length of Lateral', field: 'lengthOfLateral' },
    { title: 'rrcField', field: 'rrcField' },
    { title: 'Payor', field: 'payor' },
    { title: 'Operator', field: 'operator', cellStyle: { whiteSpace: 'nowrap', textAlign: 'center' } },
    { title: 'Family ID', field: 'familyID' },
    { title: 'lmslt DOI', field: 'mesDOI' }, // Kept as in original, but might be a typo? I think it's a typo
    { title: 'mpi DOI', field: 'mpiDOI' },  // Swapped fields as in original
    { title: 'sro DOI', field: 'sroDOI' },
    {
      title: 'Comments',
      field: 'notes',
      render: (rowData) => (
        <Tooltip
          title={rowData.notes || 'No comments'}
          classes={{ tooltip: classes.tooltip }}
          placement="top"
          arrow="true"
        >
          <span className={classes.commentsCell}>...</span>
        </Tooltip>
      ),
      cellStyle: { width: '100px', padding: '8px' },
    },
    {
      title: 'RCC Lease Notes', // Note: Typo in original 'RCC' vs 'RRC'
      field: 'rrcLeaseHistory',
      render: (rowData) => (
        <Tooltip
          title={rowData.rrcLeaseHistory || 'No notes'}
          classes={{ tooltip: classes.tooltip }}
          placement="top"
          arrow="true"
        >
          <span className={classes.commentsCell}>...</span>
        </Tooltip>
      ),
      cellStyle: { width: '100px', padding: '8px' },
    },
    {
      title: 'Operator History',
      field: 'operatorHistory',
      render: (rowData) => (
        <Tooltip
          title={rowData.operatorHistory || 'No history'}
          classes={{ tooltip: classes.tooltip }}
          placement="top"
          arrow="true"
        >
          <span className={classes.commentsCell}>...</span>
        </Tooltip>
      ),
      cellStyle: { width: '100px', padding: '8px' },
    },
    { title: 'Unit', field: 'unit', render: (rowData) => rowData.unit?.name || '' },
  ];

  // Custom CSV export function
  const handleExportCsv = (columns, data) => {
    const exportData = wells.map((w) => ({
      name: w.name,
      apiNumber: w.apiNumber,
      rrcLeaseNumber: w.rrcLeaseNumber,
      permitNumber: w.permitNumber || '',
      landId: w.drilledOn.map((e) => e.id).join(', '),
      abstract: w.abstract || '',
      status: w.status || '',
      spudDate: w.spudDate || '',
      rigReleaseDate: w.rigReleaseDate || '',
      dateProducing: w.dateProducing || '',
      dateInPay: w.dateInPay || '',
      OGL: w.OGL || '',
      location: w.location || '',
      decProdUnit: w.decProdUnit || '',
      dpu: w.dpu || '',
      producingFormation: w.producingFormation || '',
      lengthOfLateral: w.lengthOfLateral || '',
      rrcField: w.rrcField || '',
      payor: w.payor || '',
      operator: w.operator || '',
      familyID: w.familyID || '',
      mesDOI: w.mesDOI || '',
      mpiDOI: w.mpiDOI || '',
      sroDOI: w.sroDOI || '',
      notes: w.notes ? w.notes.replace(/(?:\r\n|\r|\n)/g, '') : '',
      rrcLeaseNotes: w.rrcLeaseHistory ? w.rrcLeaseHistory.replace(/(?:\r\n|\r|\n)/g, '') : '',
      operatorHistory: w.operatorHistory ? w.operatorHistory.replace(/(?:\r\n|\r|\n)/g, '') : '',
      unit: w.unit?.name || '',
    }));

    const headerRow = headers.map((h) => `"${h.label}"`).join(',');
    const dataRows = exportData.map((row) =>
      headers
        .map((h) => {
          const value = row[h.key] ?? '';
          return `"${value.toString().replace(/"/g, '""')}"`;
        })
        .join(',')
    );
    const csvContent = [headerRow, ...dataRows].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'InactiveWells.csv';
    link.click();
  };

  return (
    <div style={{maxWidth: '100vw'}}>
      <MaterialTable
        columns={columns}
        data={wells.map((w) => ({ ...w }))}
        // Set highlightedRow to table data ID( used in rowStyle function in the options section)
        onRowClick={(event, rowData) => {
          setHighlightedRow(rowData.tableData?.id);
        }}
        title=""
        components={{
          Toolbar: (props) => (
            <div
              style={{
                backgroundColor: '#1976d2',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <MTableToolbar {...props} />
            </div>
          ),
        }}
        options={{
          headerStyle: {
            backgroundColor: '#1976d2',
            color: '#FFF',
            textAlign: 'center',
            justifyContent: 'center',
            whiteSpace: 'pre',
          },
          selection: false,
          exportButton: true,
          exportCsv: handleExportCsv,
          paging: false, // Render all rows
          maxBodyHeight: '80vh', // Optional: Vertical scroll if too tall
          searchFieldStyle: {
            marginLeft: '-24px',
            border: 'none',
            paddingTop: '4px',
            paddingBottom: '4px',
            paddingLeft: '8px',
            paddingRight: '8px',
            borderRadius: '4px',
            backgroundColor: '#fff',
            color: '#000',
          },
          searchFieldAlignment: 'left',
          toolbarButtonAlignment: 'left',
          rowStyle: (rowData) => ({
            backgroundColor:
              rowData.tableData?.id === highlightedRowID ? '#c5c5c5' : 'white',
            transition: 'background-color 0.3s ease',
            height: '48px', // Fixed row height
          }),
        }}
      />
    </div>
  );
};

export default InactiveWells;